import React, { useState } from 'react';
import { TripSummary } from '../../types/tripSummary';
import { Mail, MessageSquare } from 'lucide-react';
import QuoteRequestForm from './QuoteRequestForm';
import { QuoteFormData } from '../../types/quote';

interface TripSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  summary: TripSummary | null;
  placePhotos?: Record<string, string>;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onEmailSummary?: (additionalEmail?: string) => Promise<void>;
  onRequestQuote?: (formData: QuoteFormData) => Promise<{ success: boolean; deepLink?: string; message: string; }>;
  tripId?: string;
  tripName?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  numAdults?: number;
  numChildren?: number;
}

const TripSummaryModal: React.FC<TripSummaryModalProps> = ({
  isOpen,
  onClose,
  summary,
  placePhotos = {},
  loading,
  error,
  onRetry,
  onEmailSummary,
  onRequestQuote,
  tripId,
  tripName,
  startDate,
  endDate,
  numAdults,
  numChildren
}) => {
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  
  // Check sidebar expansion state to position modal correctly
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebar_expanded') === 'true';
  });
  
  // Listen for sidebar expansion changes
  React.useEffect(() => {
    const checkSidebarState = () => {
      setSidebarExpanded(localStorage.getItem('sidebar_expanded') === 'true');
    };
    
    // Check initially and on storage changes
    checkSidebarState();
    window.addEventListener('storage', checkSidebarState);
    
    // Also check periodically in case localStorage changes in same tab
    const interval = setInterval(checkSidebarState, 500);
    
    return () => {
      window.removeEventListener('storage', checkSidebarState);
      clearInterval(interval);
    };
  }, []);

  // Dynamic loading messages
  const loadingMessages = [
    { icon: '🔍', text: 'Analyzing your itinerary...', subtext: 'Reviewing places and activities' },
    { icon: '🗺️', text: 'Mapping your journey...', subtext: 'Calculating optimal routes' },
    { icon: '🌤️', text: 'Checking weather patterns...', subtext: 'Finding best visit times' },
    { icon: '🎯', text: 'Optimizing your schedule...', subtext: 'Arranging activities perfectly' },
    { icon: '📝', text: 'Crafting recommendations...', subtext: 'Personalizing tips for you' },
    { icon: '✨', text: 'Finalizing your summary...', subtext: 'Almost ready!' }
  ];

  // Rotate loading messages every 3 seconds
  React.useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0); // Reset when not loading
    }
  }, [loading, loadingMessages.length]);

  if (!isOpen) return null;

  const handleEmailClick = () => {
    if (!summary) return;
    setShowEmailInput(true);
    setEmailError('');
  };

  const handleSendEmail = async () => {
    if (!onEmailSummary) return;
    
    // Validate email if provided
    if (additionalEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(additionalEmail)) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }

    setIsSending(true);
    setEmailError('');
    
    try {
      await onEmailSummary(additionalEmail || undefined);
      setShowEmailInput(false);
      setAdditionalEmail('');
    } catch (err: any) {
      setEmailError(err.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleQuoteSubmit = async (formData: QuoteFormData) => {
    if (!onRequestQuote) return;
    
    try {
      const result = await onRequestQuote(formData);
      
      // If there's a deep link (WhatsApp/Telegram), open it
      if (result && result.deepLink) {
        // For mobile, use location.href for better app opening
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          window.location.href = result.deepLink;
        } else {
          // For desktop, open in new tab
          window.open(result.deepLink, '_blank');
        }
      }
      
      setShowQuoteForm(false);
    } catch (err: any) {
      // Error is handled in the form component
      throw err;
    }
  };

  // Calculate sidebar width for desktop positioning
  const sidebarWidth = sidebarExpanded ? 192 : 64; // 192px expanded (w-48), 64px collapsed (w-16)
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
  
  // On desktop: modal should cover the drawer panel (left 50% of Plan area after sidebar)
  const modalWidth = isDesktop 
    ? `calc((100vw - ${sidebarWidth}px) / 2)` // Half of available width after sidebar
    : '100%';
  
  return (
    <div 
      className="fixed bottom-16 md:bottom-0 md:inset-y-0 left-0 right-0 md:right-auto z-[60] md:z-[100] flex items-center justify-center md:justify-start"
      onClick={onClose}
      style={{
        // Mobile: leave space for bottom nav (64px = bottom-16)
        // Desktop: start after the sidebar, full height
        left: isDesktop ? `${sidebarWidth}px` : 0,
        top: isDesktop ? 0 : 0,
      }}
    >
      {/* Backdrop - only on drawer panel area for desktop, doesn't cover bottom nav on mobile */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        style={{
          // On desktop, backdrop only covers the drawer panel width
          width: isDesktop ? modalWidth : '100%',
        }}
      />
      
      {/* Modal Card - covers drawer panel (left half of Plan area) on desktop, full screen minus bottom nav on mobile */}
      <div 
        className="relative h-full bg-white md:rounded-none shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: modalWidth,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-700 bg-gradient-to-r from-slate-800 to-slate-700 relative">
          {/* Accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-purple-500"></div>
          <div className="flex items-center gap-2 md:gap-4 flex-1">
            <h2 className="text-base md:text-2xl font-bold text-white">
              {summary?.tripTagline || 'Trip Summary & Tips'}
            </h2>
            
            {/* Action Buttons */}
            {summary && !loading && !error && (
              <div className="flex items-center gap-2">
                {/* Animated Email Button */}
                {onEmailSummary && (
                  <button
                    onClick={handleEmailClick}
                    className="group relative overflow-hidden bg-white text-orange-600 px-2 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-pulse hover:animate-none"
                    style={{
                      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                    }}
                  >
                    <div className="flex items-center gap-1 md:gap-2">
                      <Mail className="w-3 h-3 md:w-5 md:h-5" />
                      <span className="hidden md:inline">Email Me This</span>
                      <span className="md:hidden">Email</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                  </button>
                )}
                
                {/* Get Quote Button */}
                {onRequestQuote && (
                  <button
                    onClick={() => setShowQuoteForm(true)}
                    className="group relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 text-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    <div className="flex items-center gap-1 md:gap-2">
                      <MessageSquare className="w-3 h-3 md:w-5 md:h-5" />
                      <span className="hidden md:inline">Get Quote</span>
                      <span className="md:hidden">Quote</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="p-1.5 md:p-2 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Email Input Inline */}
        {showEmailInput && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 md:px-6 py-3 md:py-4">
            <div className="max-w-2xl">
              <label className="block text-xs md:text-sm font-semibold text-blue-900 mb-2">
                Email this summary (optional: add additional recipient)
              </label>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="email"
                  value={additionalEmail}
                  onChange={(e) => setAdditionalEmail(e.target.value)}
                  placeholder="additional@email.com (optional)"
                  className="flex-1 px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSending}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSendEmail}
                    disabled={isSending}
                    className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 md:px-6 py-1.5 md:py-2 text-sm md:text-base rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {isSending ? (
                      <>
                        <svg className="w-3 h-3 md:w-4 md:h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3 h-3 md:w-4 md:h-4" />
                        Send
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowEmailInput(false);
                      setAdditionalEmail('');
                      setEmailError('');
                    }}
                    className="px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-gray-600 hover:text-gray-800 font-medium"
                    disabled={isSending}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {emailError && (
                <p className="text-red-600 text-xs md:text-sm mt-2">{emailError}</p>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {showQuoteForm ? (
          <div className="flex-1 overflow-hidden">
            <QuoteRequestForm
              tripId={tripId || ''}
              tripName={tripName || summary?.tripTagline || 'Your Trip'}
              tripTagline={summary?.tripTagline}
              startDate={startDate || new Date()}
              endDate={endDate || new Date()}
              numAdults={numAdults}
              numChildren={numChildren}
              onClose={() => setShowQuoteForm(false)}
              onSubmit={handleQuoteSubmit}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
            {loading && (
            <div className="flex flex-col items-center justify-center py-16 md:py-20">
              {/* Animated spinner */}
              <div className="relative mb-6 md:mb-8">
                <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center text-2xl md:text-3xl animate-pulse">
                  {loadingMessages[loadingMessageIndex].icon}
                </div>
              </div>
              
              {/* Dynamic status message */}
              <div className="text-center max-w-md transition-all duration-500">
                <p className="text-gray-800 text-base md:text-xl font-semibold mb-2 animate-fade-in">
                  {loadingMessages[loadingMessageIndex].text}
                </p>
                <p className="text-gray-500 text-xs md:text-sm animate-fade-in">
                  {loadingMessages[loadingMessageIndex].subtext}
                </p>
              </div>
              
              {/* Progress indicator */}
              <div className="mt-6 md:mt-8 flex gap-1.5 md:gap-2">
                {loadingMessages.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 md:h-2 rounded-full transition-all duration-300 ${
                      index === loadingMessageIndex
                        ? 'w-8 md:w-10 bg-orange-500'
                        : 'w-1.5 md:w-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-16 md:py-20">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 md:w-8 md:h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-red-600 text-base md:text-lg font-semibold mb-2">Failed to Generate Summary</p>
              <p className="text-gray-600 text-sm md:text-base text-center max-w-md mb-4 px-4">{error}</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="px-4 md:px-6 py-1.5 md:py-2 text-sm md:text-base bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {summary && !loading && !error && (
            <div className="max-w-4xl mx-auto">
              {/* Overall Summary */}
              <div className="mb-4 md:mb-8 p-4 md:p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg md:rounded-xl border border-orange-200">
                <h3 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-3 flex items-center">
                  <span className="mr-2 text-sm md:text-base">✨</span>
                  Trip Overview
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm md:text-lg">
                  {summary.overallSummary}
                </p>
              </div>

              {/* Daily Summaries */}
              <div className="space-y-4 md:space-y-6">
                {summary.days.map((day, index) => (
                  <div
                    key={day.dayNumber}
                    className="bg-white border border-gray-200 rounded-lg md:rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Day Header */}
                    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 md:px-6 py-4 md:py-6 border-b border-gray-700 relative">
                      {/* Accent bar */}
                      <div className="absolute top-0 left-0 right-0 h-0.5 md:h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-purple-500"></div>
                      
                      <div className="flex items-center justify-between mb-2 md:mb-3">
                        <h3 className="text-lg md:text-2xl font-bold text-white tracking-tight">
                          {day.isMultiDayBlock && day.blockDayIndices && day.blockDayIndices.length > 0
                            ? `Days ${day.blockDayIndices.map(i => i + 1).join('-')}`
                            : `Day ${day.dayNumber}`
                          }
                        </h3>
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-teal-500/20 text-teal-300 text-xs md:text-sm font-semibold rounded-full whitespace-nowrap border border-teal-500/30">
                          ⏰ {day.suggestedDepartureTime}
                        </span>
                      </div>
                      <h4 className="text-base md:text-xl font-semibold text-teal-300 mb-2 md:mb-3 tracking-tight">
                        {day.title}
                      </h4>
                      <p className="text-slate-200 leading-relaxed text-sm md:text-base">
                        {day.summary}
                      </p>
                    </div>

                    {/* Day Content */}
                    <div className="p-4 md:p-6">
                      {/* Dress Code */}
                      <div className="mb-3 md:mb-4 p-3 md:p-4 bg-slate-50 rounded-lg border-l-3 border-l-purple-500 border border-slate-200">
                        <p className="text-xs md:text-sm font-semibold text-slate-700 mb-1">
                          <span className="text-purple-500">👕</span> Dress Code
                        </p>
                        <p className="text-slate-600 text-sm md:text-base">{day.dressCode}</p>
                      </div>

                      {/* Day Tips */}
                      <div className="mb-4 md:mb-6 p-3 md:p-4 bg-teal-50 rounded-lg border-l-3 border-l-teal-500 border border-teal-100">
                        <p className="text-xs md:text-sm font-semibold text-teal-700 mb-2 uppercase tracking-wide">
                          💡 Tips for the Day
                        </p>
                        <ul className="space-y-2 md:space-y-2.5">
                          {day.tips.map((tip, tipIdx) => (
                            <li
                              key={tipIdx}
                              className="flex items-start text-teal-800 text-sm md:text-base"
                            >
                              <span className="mr-2 mt-0.5 md:mt-1 text-teal-500 font-bold">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Transportation */}
                      {day.transportation && (
                        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-blue-50 rounded-lg border-l-3 border-l-blue-500 border border-blue-100">
                          <p className="text-xs md:text-sm font-semibold text-blue-700 mb-2 uppercase tracking-wide">
                            🚗 Transportation
                          </p>
                          <p className="text-blue-800 text-sm md:text-base mb-3">{day.transportation.summary}</p>
                          
                          {day.transportation.tips && day.transportation.tips.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-blue-600 mb-1">Tips:</p>
                              <ul className="space-y-1">
                                {day.transportation.tips.map((tip, tipIdx) => (
                                  <li
                                    key={tipIdx}
                                    className="flex items-start text-blue-700 text-xs md:text-sm"
                                  >
                                    <span className="mr-2 text-blue-500">•</span>
                                    <span>{tip}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {day.transportation.warnings && day.transportation.warnings.length > 0 && (
                            <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                              <p className="text-xs font-medium text-amber-700 mb-1">⚠️ Warnings:</p>
                              <ul className="space-y-1">
                                {day.transportation.warnings.map((warning, warnIdx) => (
                                  <li
                                    key={warnIdx}
                                    className="flex items-start text-amber-700 text-xs md:text-sm"
                                  >
                                    <span className="mr-2">•</span>
                                    <span>{warning}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Places */}
                      <div>
                        <p className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">
                          📍 Places to Visit
                        </p>
                        <div className="space-y-3 md:space-y-4">
                          {day.places.map((place, placeIdx) => {
                            const photoUrl = placePhotos[place.placeName];
                            
                            return (
                            <div
                              key={placeIdx}
                              className="p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex gap-3 md:gap-4">
                                {/* Thumbnail Image */}
                                {photoUrl && (
                                  <div className="flex-shrink-0">
                                    <img
                                      src={photoUrl}
                                      alt={place.placeName}
                                      className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg border border-gray-300"
                                    />
                                  </div>
                                )}
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between mb-1.5 md:mb-2">
                                    <h5 className="font-semibold text-gray-800 text-sm md:text-lg">
                                      {placeIdx + 1}. {place.placeName}
                                    </h5>
                                    <span 
                                      className="text-xs md:text-sm text-gray-600 bg-white px-2 md:px-3 py-0.5 md:py-1 rounded-full border border-gray-300 whitespace-nowrap ml-2"
                                      title={place.estimatedDuration}
                                    >
                                      ⏱️ {place.estimatedDuration?.length > 12 ? place.estimatedDuration.substring(0, 12) + '...' : place.estimatedDuration}
                                    </span>
                                  </div>
                              
                              <div className="flex items-center text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
                                <svg
                                  className="w-3 h-3 md:w-4 md:h-4 mr-1 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="font-medium">Best time:</span>
                                <span className="ml-1">{place.bestTimeToVisit}</span>
                              </div>

                              {place.tips.length > 0 && (
                                <ul className="space-y-1">
                                  {place.tips.map((placeTip, placeTipIdx) => (
                                    <li
                                      key={placeTipIdx}
                                      className="flex items-start text-xs md:text-sm text-gray-600"
                                    >
                                      <span className="mr-2 text-green-500">→</span>
                                      <span>{placeTip}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                                </div>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TripSummaryModal;

